/**
 * ⚠️ FIXTURE-USE BOUNDARY (Phase 5 PR 5.1)
 *
 * The shapes (`Day`, `WeeklyPlan`, `TodaySession`) and the demo data
 * (`ramiroWeek`, `ramiroToday`, `emptyPlan`) below are exposed for:
 *
 *   ✅ tests (vitest snapshot + adapter unit tests)
 *   ✅ storybook-style design preview (`/v2/today?user=ramiro` etc.)
 *   ✅ local development / pre-auth previews
 *
 *   ❌ Production routes — DO NOT import the demo data into a route
 *      that's reachable by signed-in users without `?user=` set. The
 *      production path uses real-data hooks (`useTodaySession`,
 *      `useWeeklyPlan`) which return `null` while loading and during
 *      empty-states; the dashboard renders skeletons / empty-state UX
 *      in those cases. Flashing another athlete's fixture data during
 *      load is a trust regression we explicitly avoid (Phase 5 plan
 *      §"Decision 4").
 *
 * The TYPES (`Day`, `WeeklyPlan`, `TodaySession`) ARE the contract the
 * hooks adapt DB rows into — they're load-bearing and may be imported
 * anywhere.
 */
import { addDays, demoAnchorMonday, fmtWeekRange } from './demoAnchor';
import type { HeroActualSubline } from '../lib/actualForPlanDay';

export type Day = {
  dayShort: 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';
  dayNumber: number;
  label: string; // "Easy", "Rest", "Tempo", "Long"
  mileage: string; // "3 mi", "—"
  pace?: string; // "9:30 /mi"
  today?: boolean;
  rest?: boolean;
  hard?: boolean;
  /**
   * Phase 6c.1 — plan_day UUID for completion logging. Real-data path
   * populates this from `useWeeklyPlan`; fixture / demo path leaves it
   * undefined so click handlers no-op gracefully.
   */
  id?: string;
  /**
   * Phase 6c.1 — completion status for this day. Real-data path
   * populates from `plan_days.completion_status`; fixture path
   * undefined → rendered as 'upcoming' (the default UI state).
   *
   * 'completed' → show ✓ + dim row (done)
   * 'skipped'   → show line-through + dim (vanilla parity — write-side
   *                deferred to a follow-up; React MVP toggles
   *                'completed' ↔ 'upcoming' only)
   * 'upcoming'  → default render
   */
  completionStatus?: 'completed' | 'skipped' | 'upcoming' | null;
  /**
   * RPE restore (2026-06-09) — perceived effort 1-10 logged via the
   * post-completion RpePrompt (vanilla `plan_days.rpe_value` parity).
   * Real-data path populates from the DB; fixtures leave it undefined.
   */
  rpe?: number | null;
  /**
   * B309 — the plan day's session text (`plan_days.details`), shown in the
   * day details panel, with its km / mile distances stated in the athlete's
   * own unit (display only; the stored text is untouched). Real-data adapter
   * only; fixtures leave it undefined.
   */
  details?: string | null;
  /**
   * B309 fix round — the planned distance in the athlete's own unit when that
   * differs from `mileage` (which is miles for every athlete): "10.2 km" for a
   * km athlete. Absent for a mile athlete, a time-based day and a rest day, so
   * consumers read `mileageInUnit ?? mileage`. The details title reads it.
   */
  mileageInUnit?: string;
  /**
   * B309 — the distance logged for the day (stored actual, or an imported
   * workout's credit), in the athlete's display unit, rounded to 0.1. Set only
   * when above zero (a stored 0 encodes a skip, not a run). Independent of the
   * B-19 display flag: the details panel shows what was logged, while the
   * grid's "X of Y mi" pairing stays behind its flag. Fixtures leave it unset.
   */
  actualDistance?: { value: number; unit: 'mi' | 'km' } | null;
  /**
   * B-19 (b19_actuals_v1) — planned-vs-actual pairing for a day with a stored
   * actual. Populated ONLY by the real-data adapter when the flag is ON and a
   * valid `plan_days.actual_distance` exists; fixtures / demo / flag-off leave
   * it undefined so every surface renders byte-identical (planned only).
   *
   * `copy` is the shared neutral-factual "X of Y mi" string (Y2 no-gamification,
   * §4 — no delta/percentage/celebration). `state` drives at most a hairline
   * label: over/under/match carry the SAME visual weight (never a reward).
   */
  actual?: {
    copy: string;
    state: 'match' | 'over' | 'under';
    /**
     * B-19 Today-hero completed-state sub-line (frame §1-§8). A DIFFERENT
     * grammar from the list `copy` ("You ran X mi · planned N", not "X of Y
     * mi") — derived by `heroActualSubline`. Only the Today-hero reads it (the
     * WeekPlan grid renders `copy`); null/undefined → no sub-line mounts.
     */
    hero?: HeroActualSubline | null;
  } | null;
};

export type WeeklyPlan = {
  dateRange: string; // "Apr 20 to Apr 26"
  weekMileageDone: number;
  weekMileageTarget: number;
  /**
   * B338 — the same two figures in the athlete's own unit, set by the real-data
   * adapter (`useWeeklyPlan`). `weekMileage*` stay miles for every athlete; a surface
   * that prints a distance reads these when `distanceUnit` is 'km'. Fixtures leave
   * them unset, which reads as miles.
   */
  distanceUnit?: 'mi' | 'km';
  weekDistanceDone?: number;
  weekDistanceTarget?: number;
  status: string; // "3 runs done · 3 to go"
  days: Day[];
};

/**
 * PR-G (DD-6/E5) — the demo week derives its dates from the real current
 * week (was frozen at Apr 20–26, drifting away from the hero's week as
 * time passed). Workout content stays the designed showcase; only the
 * calendar numbers + range are live. The demo "today" is the week's
 * Monday — the Easy-run day — so the hero's "Easy run, 3 miles." always
 * matches the grid's today-marker.
 */
export function demoWeek(now: Date = new Date()): WeeklyPlan {
  const monday = demoAnchorMonday(now);
  const dayNum = (i: number) => addDays(monday, i).getDate();
  return {
    dateRange: fmtWeekRange(monday),
    weekMileageDone: 19,
    weekMileageTarget: 22,
    status: '3 runs done · 3 to go',
    days: [
      { dayShort: 'MON', dayNumber: dayNum(0), label: 'Easy', mileage: '3 mi', pace: '9:30 /mi', today: true },
      { dayShort: 'TUE', dayNumber: dayNum(1), label: 'Rest', mileage: '—', rest: true },
      { dayShort: 'WED', dayNumber: dayNum(2), label: 'Tempo', mileage: '4 mi', pace: '7:45 /mi', hard: true },
      { dayShort: 'THU', dayNumber: dayNum(3), label: 'Rest', mileage: '—', rest: true },
      { dayShort: 'FRI', dayNumber: dayNum(4), label: 'Easy', mileage: '3 mi', pace: '9:30 /mi' },
      { dayShort: 'SAT', dayNumber: dayNum(5), label: 'Long', mileage: '7 mi', pace: '9:45 /mi', hard: true },
      { dayShort: 'SUN', dayNumber: dayNum(6), label: 'Rest', mileage: '—', rest: true },
    ],
  };
}

export const ramiroWeek: WeeklyPlan = demoWeek();

/** New user — no plan yet. Triggers <TodayHero variant="firstRun"> +
 *  NoPlan Championship. */
export const emptyPlan: WeeklyPlan | null = null;

export type TodaySession = {
  phaseLabel: string; // "Today · Week 2 · Base"
  intensityLabel: string; // "Easy"
  /** Split title so the em phrase can render as <em> with --today color. */
  titleLead: string; // "Easy run,"
  titleEm: string; // "3 miles"
  titleTrail: string; // "."
  subtitle: string; // "Conversational pace..."
  stats: { value: string; label: string }[];
  routeLabel?: string; // "Hillview Loop · saved"
  /** Optional per-session coach note embedded in the hero (Training coach
   *  speaks ABOUT today's session; motivational coach lives in Championship). */
  heroCoachNote?: {
    coachName: string;
    timeAgo: string;
    text: string;
  };
};

export const ramiroToday: TodaySession = {
  phaseLabel: 'Today · Week 2 · Base',
  intensityLabel: 'Easy',
  titleLead: 'Easy run,',
  titleEm: '3 miles',
  titleTrail: '.',
  subtitle: 'Conversational pace. Nose-breathing easy.',
  stats: [
    { value: '3', label: 'miles' },
    { value: '9:30', label: 'pace /mi' },
    { value: '28', label: 'min approx' },
    { value: 'RPE 4', label: 'conversational' },
  ],
  routeLabel: 'Hillview Loop · saved',
  heroCoachNote: {
    coachName: 'Training Coach',
    timeAgo: 'just now',
    text: "After Friday's 1600m setback, today resets the engine. Keep it conversational. We build tempo on Wednesday.",
  },
};
